import React, { useEffect, useState } from "react";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import styled from "styled-components";

import UserDataComponent from "./components/user.data.component";
import DeviceComponent from "./components/device.component";
import PaymentComponent from "./components/payment.component";
import StatusPaymentComponent from "./components/status-payment.component";
import PrimaryButton from "../../../../components/buttons/PrimaryButton";
import { IUseCreatePolicyAgents } from "../../../../services/microservice/micelu/create/use-create-micelu.interface";
import { useModalContext } from "../../../../providers/modal/modal.provider";
import ModalHeader from "../../../../components/claims/components/ModalHeader";
import ModalBody from "../../../../components/claims/components/ModalBody";
import html2canvas from "html2canvas";
import { useCreateSimplyAcincoContext } from "./provider/createSimplyAcinco.provider";

/* IMPORTS PARA PRODUCCIÓN - Comentado para demo
import {
  useCreatePolicyAgents,
  usePreCreatePolicyPaymentAgents,
} from "../../../../services/microservice/micelu/create/use-create-micelu";
import { useVerifyDeviceBySponsor } from "../../../../services/microservice/micelu/device/use-device";
*/

interface IInputs {
  name: string;
  typeDocument: string;
  numberDocument: string;
  email: string;
  imei: string;
  insuredValue: string;
  plan: number;
  manufacturer: {
    id_system: string;
    nombre: string;
  };
  model: {
    device_name: string;
    reference_tac: string;
  };
  valuePolicie: string;
  confirm: boolean;
}

const messageRequired = " Requerido";

const splitFullName = (
  fullName: string,
): { firstName: string; lastName: string } => {
  const words = fullName.trim().split(/\s+/).filter(Boolean);

  if (words.length === 0) {
    return { firstName: "", lastName: "" };
  }

  if (words.length === 1) {
    return { firstName: words[0], lastName: "" };
  }

  if (words.length === 2) {
    return { firstName: words[0], lastName: words[1] };
  }

  if (words.length === 3) {
    return { firstName: words[0], lastName: `${words[1]} ${words[2]}` };
  }

  // 4 o más palabras: 2 nombres, resto apellidos
  return {
    firstName: `${words[0]} ${words[1]}`,
    lastName: words.slice(2).join(" ") || "",
  };
};

const LabelCheckbox = styled.label`
  > input[type="checkbox"]:checked + i {
    background: "#000000" !important;
  }
`;

const schemaValidation = yup.object().shape({
  name: yup.string().required(messageRequired),
  typeDocument: yup.string().required(messageRequired),
  numberDocument: yup
    .string()
    .matches(/^\d+$/, { message: messageRequired })
    .required(messageRequired),
  email: yup.string().email().required(messageRequired),
  imei: yup
    .string()
    .min(15, { message: messageRequired })
    .max(15, { message: messageRequired })
    .matches(/^\d+$/, { message: messageRequired })
    .required(messageRequired),
  insuredValue: yup
    .string()
    .matches(/^\$\d+(?:,\d+)*$/, { message: messageRequired })
    .test("min-value", "Mínimo $2,000", (value) => {
      if (!value) return false;
      const numericValue = Number.parseInt(
        value.replaceAll("$", "").replaceAll(",", ""),
        10,
      );
      return numericValue > 2000;
    })
    .required(messageRequired),
  manufacturer: yup
    .object({
      id_system: yup.string().required(" Requerido"),
      nombre: yup.string().required(" Requerido"),
    })
    .required(" Requerido"),
  model: yup
    .object({
      device_name: yup.string().required(" Requerido"),
      reference_tac: yup.string().required(" Requerido"),
    })
    .required(" Requerido")
    .typeError(" Requerido"),
  plan: yup.number().required(messageRequired).typeError(messageRequired),
  confirm: yup.boolean(),
});

const CreateSimplyComponent = () => {
  const { setShow, modalRef } = useModalContext();
  const {
    step,
    usePaymentGateway,
    setStep,
    setUsePaymentGateway,
    setInfoPayment,
    setFormData,
    setResetFormCallback,
  } = useCreateSimplyAcincoContext();
  const [selectedPlan, setSelectedPlan] = useState<number>(null);
  const [planData, setPlanData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);

  /* HOOKS PARA PRODUCCIÓN - Comentado para demo
  const { mutateAsync: create, isLoading: isCreating } =
    useCreatePolicyAgents();
  const { mutateAsync: preCreatePolicyPayment, isLoading: isPreCreating } =
    usePreCreatePolicyPaymentAgents();
  const { mutateAsync: verifyDevice, isLoading: isLoadingVerifyDevice } =
    useVerifyDeviceBySponsor();
  */

  const {
    control,
    formState: { errors },
    handleSubmit,
    watch,
    setValue,
    clearErrors,
    getValues,
    reset,
    register,
  } = useForm({
    resolver: yupResolver(schemaValidation),
    defaultValues: {
      name: "",
      typeDocument: "CEDULA_CIUDADANIA",
      numberDocument: "",
      email: "",
      imei: "",
      insuredValue: "",
      plan: undefined,
      confirm: false,
    },
  });

  // Registrar función reset del formulario en el provider
  useEffect(() => {
    setResetFormCallback(() => reset);
  }, [reset, setResetFormCallback]);

  const onSubmit = async (val: IInputs) => {
    // Validar checkbox de confirmación solo si NO usa pasarela de pagos
    if (!usePaymentGateway && !val.confirm) {
      setShow((prevState) => ({
        ...prevState,
        visible: true,
        header: <ModalHeader success={false} />,
        content: (
          <ModalBody
            title="Confirmación requerida"
            message="Debes confirmar que ya facturaste el producto al cliente para continuar."
          />
        ),
        onAccept: () =>
          setShow((prevState) => ({ ...prevState, visible: false })),
        titleOnAccept: "Aceptar",
        withClass: "flex",
        showBtnAccept: true,
      }));
      return;
    }

    setIsLoading(true);

    const { firstName, lastName } = splitFullName(val.name);
    const cleanInsuredValue = val.insuredValue
      .replaceAll("$", "")
      .replaceAll(",", "");
    const data: IUseCreatePolicyAgents = {
      client: {
        firstName,
        lastName,
        genderId: "POR_ACTUALIZAR",
        identification: {
          type: val.typeDocument,
          number: val.numberDocument,
        },
        email: val.email,
        address: "NA",
      },
      planId: selectedPlan,
      device: {
        line: "0000000000",
        imei: val.imei,
        referenceTac: val.model.reference_tac,
      },
      sponsorId: "GREENELECTRONICS",
      priceOptionId: val.plan,
      insuredValue: cleanInsuredValue,
    };

    try {
      // MODO DEMO: Simular verificación de dispositivo
      const resVerifyDevice = { data: { message: "Ok" } };

      /* CÓDIGO REAL - Comentado para demo
      const resVerifyDevice = await verifyDevice({
        imei: val.imei,
        manufacturer: val.manufacturer.nombre,
        model: val.model.device_name,
        sponsorID: "GREENELECTRONICS",
      });
      */

      if (resVerifyDevice.data.message === "Ok") {
        // Si usa pasarela de pagos
        if (usePaymentGateway) {
          // MODO DEMO: Simular preCreatePolicyPayment
          const demoPregenerate = {
            data: {
              pregeneratePolicyId: `PREGEN-${val.imei}-${Date.now()}`,
            },
          };

          /* CÓDIGO REAL - Comentado para demo
          const res = await preCreatePolicyPayment(data);
          */

          if (demoPregenerate) {
            // Guardar datos del formulario y pregenerateId
            setFormData({
              ...val,
              plan: planData,
              priceOptionId: val.plan,
            });
            setInfoPayment({
              pregeneratePolicyId: demoPregenerate.data.pregeneratePolicyId,
            });
            setIsLoading(false);
            setStep(2); // Ir a Payment
          }
        } else {
          // MODO DEMO: Simular creación de póliza directa
          const demoPolicy = {
            data: {
              policy: {
                id: `DEMO-POL-${val.imei}-${Date.now()}`,
              },
            },
          };

          /* CÓDIGO REAL - Comentado para demo
          const res = await create(data);
          */

          if (demoPolicy) {
            setIsLoading(false);
            setShow((prevState) => ({
              ...prevState,
              visible: true,
              header: <ModalHeader success={true} />,
              content: (
                <ModalBody
                  title="Creación póliza exitosa (DEMO)"
                  message={`La póliza DEMO se creó exitosamente con id: ${demoPolicy.data.policy.id}`}
                />
              ),
              onAccept: clearProcess,
              titleOnAccept: "Aceptar",
              withClass: "flex",
              showBtnAccept: true,
            }));
          }
        }
      }
    } catch (error) {
      setIsLoading(false);
      if (error.response.status === 400) {
        setShow((prevState) => ({
          ...prevState,
          visible: true,
          header: <ModalHeader success={false} />,
          content: (
            <ModalBody
              title=" No fue posible crear la póliza"
              message={error?.response.data.error.message}
            />
          ),
          onAccept: clearProcess,
          titleOnAccept: "Aceptar",
          withClass: "flex",
          showBtnAccept: true,
        }));
      } else {
        setShow((prevState) => ({
          ...prevState,
          visible: true,
          header: <ModalHeader success={false} />,
          content: (
            <ModalBody
              title=" No fue posible crear la póliza"
              message="Acaba de ocurrir un problema, lo sentimos."
            />
          ),
          onAccept: clearProcess,
          titleOnAccept: "Aceptar",
          withClass: "flex",
          showBtnAccept: true,
        }));
      }
    }
  };

  const clearProcess = async () => {
    await downloadScreenShoot();
    reset();
    setShow((prevState) => ({
      ...prevState,
      visible: false,
    }));
  };

  const downloadScreenShoot = async () => {
    const newDate = new Date();
    const formattedDate = newDate
      .toISOString()
      .split("T")[0]
      .replaceAll("-", "");
    if (!modalRef.current) return;

    const canvas = await html2canvas(modalRef.current);
    const dataUrl = canvas.toDataURL("image/png");

    const link = document.createElement("a");
    link.href = dataUrl;
    link.download = `${getValues("imei")}GREENELECTRONICS${formattedDate}.png`;
    link.click();
  };

  return (
    <>
      {step === 1 && (
        <form onSubmit={handleSubmit(onSubmit)}>
          <UserDataComponent control={control} errors={errors} />
          <DeviceComponent
            control={control}
            watch={watch}
            clearErrors={clearErrors}
            setValue={setValue}
            errors={errors}
            setSelectedPlan={setSelectedPlan}
            setPlanData={setPlanData}
          />
          <div className="col-span-2 w-full rounded-xl bg-gray p-3">
            <LabelCheckbox>
              <input
                id="usePaymentGateway"
                name="usePaymentGateway"
                type="checkbox"
                className="mt-5"
                checked={usePaymentGateway}
                onChange={(e) => setUsePaymentGateway(e.target.checked)}
              />
              <i></i> ¿Usar pasarela de pagos?
            </LabelCheckbox>
          </div>
          {!usePaymentGateway && (
            <div className="col-span-2 w-full rounded-xl bg-gray p-3">
              <LabelCheckbox>
                <input
                  {...register("confirm")}
                  id="confirm"
                  name="confirm"
                  type="checkbox"
                  className="mt-5"
                />
                <i></i> Confirmo que ya facturé el producto al cliente
                <span className="text-3xl text-critical">*</span>
              </LabelCheckbox>
              <div className="mb-5 h-1">
                {errors && (
                  <p className="my-0 py-0 text-base text-critical">
                    {errors.confirm?.message}
                  </p>
                )}
              </div>
            </div>
          )}
          <div className="flex w-full justify-end">
            <PrimaryButton
              title="Asegurar"
              className={
                "text-md mt-5 w-36 rounded-3xl bg-textBlack px-5 py-1 font-normal text-whiteSoft"
              }
              type="submit"
              disabled={isLoading}
              loading={isLoading}
            />
          </div>
        </form>
      )}
      {step === 2 && <PaymentComponent />}
      {step === 3 && <StatusPaymentComponent />}
    </>
  );
};

export default CreateSimplyComponent;
